import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, TrendingUp, FileText, Handshake, Clock, AlertTriangle, History, BellRing } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";
import { money, pct, fdate, searchOpts, matchKw } from "@/lib/fmt";
import { PageHeader, StatusBadge, Empty } from "@/components/Common";
import { Autocomplete } from "@/components/Autocomplete";
import { HistoryDialog } from "@/components/HistoryDialog";
import { SalesFilter } from "@/components/SalesFilter";
import { Button } from "@/components/ui/button";
import { QuoteRowActions } from "@/components/QuoteRowActions";
import { ReminderList, useReminders } from "@/components/Reminders";

const Kpi = ({ label, value, sub, Icon, accent, testId, delay }) => (
  <div className="card-premium p-5 fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-start justify-between">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <div className={`grid h-9 w-9 place-items-center rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
    </div>
    <p className="font-num mt-3 text-2xl font-bold text-slate-900">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

const KEYS = ["display_no", "customer_name", "project_title", "sales_name", "nego_remark"];

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [kw, setKw] = useState("");
  const [sales, setSales] = useState("");
  const [hist, setHist] = useState(null);
  const [tick, setTick] = useState(0);
  const nav = useNavigate();
  const [exp, setExp] = useState(0);
  useEffect(() => { api.get("/quote-reminders", { params: { days: 7, sales_id: sales } }).then((r) => setExp(r.data.length)).catch(() => {}); }, [sales]);
  const rem = useReminders();

  useEffect(() => {
    api.get("/dashboard", { params: { sales_id: sales } }).then((r) => setD(r.data));
  }, [sales, tick]);

  const groups = useMemo(() => (d ? d.groups.filter((g) => matchKw(g, kw, KEYS)) : []), [d, kw]);
  const opts = useMemo(() => (d ? searchOpts(d.groups, KEYS) : []), [d]);
  if (!d) return <div className="text-slate-400">Memuat dashboard...</div>;
  const s = d.stats;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Ringkasan penawaran (hasil akhir setelah nego), margin & aktivitas sales.">
        <SalesFilter value={sales} onChange={setSales} testId="dashboard-sales-filter" />
      </PageHeader>
      {exp > 0 && (
        <button type="button" onClick={() => nav("/quote-reminders")} className="mb-4 flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-800 transition-colors hover:bg-amber-100 fade-up" data-testid="dashboard-expiring-card">
          <BellRing className="h-4 w-4" /><b>{exp}</b> penawaran akan habis masa berlakunya dalam 7 hari — klik untuk kirim reminder ke customer
        </button>
      )}
      {s.below_target > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 fade-up" data-testid="dashboard-below-target-alert">
          <AlertTriangle className="h-4 w-4" /><b>{s.below_target}</b> penawaran aktif dengan margin di bawah target {pct(s.min_margin)}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi delay={0} testId="kpi-total-offer" label="Total Penawaran (IDR)" value={money(s.total_offer_idr)} sub={`Cost ${money(s.total_cost_idr)}`} Icon={Wallet} accent="bg-blue-50 text-blue-800" />
        <Kpi delay={60} testId="kpi-avg-margin" label="Margin Rata-rata" value={pct(s.avg_margin)} sub={`Profit ${money(s.profit_idr)} • Target ${pct(s.min_margin)}`} Icon={TrendingUp} accent="bg-emerald-50 text-emerald-700" />
        <Kpi delay={120} testId="kpi-quotations" label="Penawaran" value={s.total_quotations} sub={`${s.open} aktif • ${s.expired} expired • ${s.deal} deal • ${s.lost} lost`} Icon={FileText} accent="bg-sky-50 text-sky-700" />
        <Kpi delay={180} testId="kpi-nego" label="Penawaran di-Nego" value={s.nego_count} sub={`${s.activities} kunjungan • ${s.high_risk} resiko tinggi`} Icon={Handshake} accent="bg-amber-50 text-amber-700" />
      </div>

      {rem.total > 0 && (
        <div className="mt-6 card-premium border-l-4 border-l-rose-500 p-6 fade-up" data-testid="dashboard-reminders">
          <h3 className="font-heading text-lg font-semibold mb-3">Pengingat ({rem.total})</h3>
          <div className="grid gap-4 md:grid-cols-1"><ReminderList data={rem} onGo={nav} /></div>
        </div>
      )}

      <div className="mt-6 card-premium p-6 fade-up">
        <h3 className="font-heading text-lg font-semibold mb-4">Penawaran vs Cost — 6 Bulan Terakhir (IDR)</h3>
        <div className="h-64" data-testid="dashboard-chart">
          <ResponsiveContainer width="100%" height={256}>
            <BarChart data={d.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}M` : v >= 1e6 ? `${(v / 1e6).toFixed(0)}jt` : v)} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />
              <Bar dataKey="penawaran" name="Penawaran" fill="#1E3A8A" radius={[6, 6, 0, 0]} />
              <Bar dataKey="cost" name="Cost" fill="#94A3B8" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 card-premium fade-up">
        <div className="flex flex-col gap-3 p-5 md:flex-row md:items-center md:justify-between">
          <h3 className="font-heading text-lg font-semibold">Data Penawaran — Hasil Akhir</h3>
          <Autocomplete value={kw} onChange={setKw} options={opts} icon placeholder="Cari no, customer, sales, remark..." testId="dashboard-search" className="md:w-80" />
        </div>
        <div className="overflow-x-auto">
          <table className="tbl w-full" data-testid="dashboard-table">
            <thead><tr><th>No Penawaran</th><th>Customer</th><th>Sales</th><th className="text-right">Penawaran Awal</th><th className="text-right">Hasil Akhir</th><th className="text-right">Margin Akhir</th><th>Remark Nego</th><th>Status</th><th /></tr></thead>
            <tbody>
              {groups.map((g, i) => (
                <tr key={g.group_id} data-testid={`dashboard-row-${i}`} className={g.below_target ? "bg-rose-50/70 shadow-[inset_3px_0_0_#e11d48]" : ""}>
                  <td><button className="font-num text-sm font-semibold text-blue-800 hover:underline" onClick={() => nav(`/quotations/${g.latest_id}`)}>{g.display_no}</button><div className="text-[11px] text-slate-400">{fdate(g.created_at)} • {g.version_label}</div></td>
                  <td><p className="font-medium">{g.customer_name}</p><p className="text-xs text-slate-400">{g.project_title}</p></td>
                  <td className="text-sm">{g.sales_name}</td>
                  <td className="text-right font-num text-slate-500">{money(g.original_total, g.currency)}</td>
                  <td className="text-right font-num font-semibold">{money(g.final_total, g.currency)}</td>
                  <td className={`text-right font-num font-semibold whitespace-nowrap ${g.below_target ? "text-rose-600" : "text-emerald-700"}`} data-testid={`dashboard-margin-${i}`}>
                    {g.below_target && <AlertTriangle className="mr-1 inline h-3.5 w-3.5" data-testid={`dashboard-below-target-${i}`} />}{pct(g.final_margin)}
                  </td>
                  <td className="max-w-[220px]">{g.has_nego ? <span className="inline-flex items-start gap-1 text-xs text-amber-800"><span className="rounded bg-amber-100 px-1.5 py-0.5 font-bold">NEGO</span>{g.nego_remark}</span> : <span className="text-xs text-slate-300">-</span>}</td>
                  <td><StatusBadge status={g.status} /></td>
                  <td className="whitespace-nowrap"><Button size="sm" variant="ghost" onClick={() => setHist(g.latest_id)} data-testid={`dashboard-history-btn-${i}`}><History className="h-4 w-4 mr-1" />Riwayat</Button><QuoteRowActions id={g.latest_id} displayNo={g.display_no} fileName={g.pdf_filename} onDeleted={() => setTick((t) => t + 1)} testPrefix={`dashboard-${i}`} locked={["deal", "lost"].includes(g.status)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!groups.length && <Empty text="Belum ada penawaran. Mulai dari menu Kalkulasi Cost." />}
        </div>
      </div>
      <div className="mt-4 flex items-center gap-4 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" />Masa berlaku penawaran 7 hari. <AlertTriangle className="h-3.5 w-3.5" />Status expired otomatis.</div>
      <HistoryDialog quoteId={hist} open={!!hist} onOpenChange={(o) => !o && setHist(null)} />
    </div>
  );
}
